import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store'; 
import { cancelSpaCommand } from "../../../redux/actions/commands.actions";
import { ModalProps } from "../../../components/common/Interfaces/Modals";
import { useSearchParams } from "react-router-dom";
import { isExpired } from "../../../utils/relativeTime";
import ModalShell, { ModalLoading, ModalTone } from "../../../components/common/Modals/ModalShell";
import { clearError } from "../../../redux/reducers/commands.reducer";

const CancelCommandModal: React.FC<ModalProps> = ({open, setOpen, object}) => {
    const searchParams = useSearchParams()[0];
    const spaCommand = object;

    const handleClose = () => {
      handleReset();
      setOpen(false);
    };

    const handleReset = () => {
      dispatch(clearError());
      setSubmitted(false);
    };

    const dispatch = useDispatch<AppDispatch>();
    const { accessToken } = useSelector((state: RootState) => state.user);
    const { error, loading } = useSelector((state: RootState) => state.commands);
    
    const [submitted, setSubmitted] = useState(false);

    const handleCancel = async (e: React.FormEvent) => {
      e.preventDefault();
      // Prevent cancellation of expired commands
      if (isExpired(spaCommand?.expires_at)) {
        return;
      }
      
      if (spaCommand?.spa_id && spaCommand?.audit_id) {
        setSubmitted(true);
        await dispatch(cancelSpaCommand({
          spa_id: spaCommand.spa_id,
          audit_id: spaCommand.audit_id,
          params: searchParams.toString()
        }));
      }
    };

    const expired = isExpired(spaCommand?.expires_at);
    const confirming = !submitted;
    const inProgress = !confirming && loading && !error;
    const failed = !confirming && !inProgress && Boolean(error);

    let title: React.ReactNode;
    let tone: ModalTone | undefined;
    let body: React.ReactNode;
    let actions: React.ReactNode;

    // Confirmation state
    if (confirming) {
      title = expired ? "Command can't be canceled" : "Cancel command?";
      tone = "warning";
      body = expired ? (
        <>
          <div className="dlg-notice warning">This command has expired and cannot be canceled.</div>
          {spaCommand?.expires_at && (
            <div className="dlg-current">
              <span>Expired</span>
              <strong>{new Date(spaCommand.expires_at).toLocaleString()}</strong>
            </div>
          )}
        </>
      ) : (
        <>
          <p>Are you sure you want to cancel this command?</p>
          {spaCommand?.command?.title && (
            <div className="dlg-current">
              <span>Command</span>
              <strong>{spaCommand.command.title}</strong>
            </div>
          )}
          {spaCommand?.spa_id && (
            <div className="dlg-current">
              <span>Spa ID</span>
              <strong className="dlg-mono">{spaCommand.spa_id}</strong>
            </div>
          )}
          {spaCommand?.expires_at && (
            <div className="dlg-current">
              <span>Expires</span>
              <strong>{new Date(spaCommand.expires_at).toLocaleString()}</strong>
            </div>
          )}
          <p>This action cannot be undone.</p>
        </>
      );
      actions = (
        <>
          <button className="btn" onClick={handleClose}>Close</button>
          {!expired && (
            <button className="btn btn-danger" onClick={handleCancel} disabled={!accessToken}>
              Cancel command
            </button>
          )}
        </>
      );
    }
    // Loading/Progress state
    else if (inProgress) {
      title = "Canceling command…";
      body = <ModalLoading label="Please wait while the command is being canceled."/>;
    }
    // Error state
    else if (failed) {
      title = "Error canceling command";
      tone = "error";
      body = <p>{error}</p>;
      actions = (
        <>
          <button className="btn" onClick={handleClose}>Close</button>
          <button className="btn btn-primary" onClick={handleReset}>Try again</button>
        </>
      );
    }
    // Success state
    else {
      title = "Command canceled";
      tone = "success";
      body = <p>Command has been successfully canceled.</p>;
      actions = <button className="btn btn-primary" onClick={handleClose}>OK</button>;
    }

    return (
      <ModalShell
        open={open}
        onClose={handleClose}
        size="sm"
        busy={inProgress}
        title={title}
        tone={tone}
        actions={actions}
      >
        {body}
      </ModalShell>
    );
};

export default CancelCommandModal;
